import type { ModelActionHistory } from '@app/api/ActionsHistory';
import type { WithActiveBranchId } from '@app/api/shared';

export enum CrmTasksEndpoints {
  GetCrmTaskById = 'GetCrmTaskById',
  DeleteCrmTaskById = 'DeleteCrmTaskById',
  CreateCrmTask = 'CreateCrmTask',
  UpdateCrmTask = 'UpdateCrmTask',
  CompletedCrmTask = 'CompletedCrmTask',
  InProgressCrmTask = 'InProgressCrmTask',
  GetCrmTasksForPeriod = 'GetCrmTasksForPeriod',
  SearchCrmTasks = 'SearchCrmTasks',
  GetSearchCrmTasks = 'GetSearchCrmTasks',
  SetOwnersForCrmTasks = 'SetOwnersForCrmTasks',
  SetCompletedForCrmTasks = 'SetCompletedForCrmTasks',
  DeleteCrmTasks = 'DeleteCrmTasks',
  GetCrmTaskFilterData = 'GetCrmTaskFilterData',
  SearchCrmTaskClients = 'SearchCrmTaskClients',
  SearchCrmTaskUsers = 'SearchCrmTaskUsers',
  CreateCrmTaskComment = 'CreateCrmTaskComment',
  UpdateCrmTaskComment = 'UpdateCrmTaskComment',
  GetCrmTaskHistoryById = 'GetCrmTaskHistoryById',
  CreateCrmTaskType = 'CreateCrmTaskType',
  EditCrmTaskType = 'EditCrmTaskType',
  DeleteCrmTaskType = 'DeleteCrmTaskType',
  GetCrmTaskTypeById = 'GetCrmTaskTypeById',
  GetCrmTaskTypes = 'GetCrmTaskTypes',
}

export enum CrmTaskStatus {
  Open = 0,
  InProgress = 1,
  Completed = 2,
  Overdue = 3,
  Canceled = 4,
}

export enum CrmTaskPeriodType {
  Today = 0,
  Tomorrow = 1,
  Week = 2,
  Month = 3,
}

export interface ModelUserNames {
  UserId: number;
  NameLast: string | null;
  NameFirst: string | null;
  NameMiddle: string | null;
  NameDative: string | null;
  NameGenitive: string | null;
}

export interface ModelClientInfo {
  Id: number;
  LeadId?: number;
  NameFirst: string | null;
  NameLast: string | null;
  Phone1: string | null;
  Phone2: string | null;
  BranchId: number;
  BranchName: string | null;
}

export interface CrmTasksModelItemEx {
  Id: number;
  IdS: string;
  Name: string;
  IsDeleted: boolean;
}

export interface ModelEnum<T> {
  Status: T;
  StatusName?: string | null;
  StatusDescription?: string | null;
}

export interface ModelCrmTaskUser {
  UserId: number;
  NameFirst: string | null;
  NameLast: string | null;
  NameMiddle: string | null;
  IsDeleted: boolean;
}

export interface ModelCrmTaskClient {
  ClientId: number;
  NameFirst: string | null;
  NameLast: string | null;
  NameMiddle: string | null;
  Phone1: string | null;
  Phone2: string | null;
  IsPhoneVerified: boolean;
  BranchId: number;
  BranchName: string | null;
}

export interface ModelCrmTaskComment {
  Id: number;
  Text: string | null;
  TimeCreated: string;
  UserCreator: ModelCrmTaskUser;
}

export interface ModelCrmTaskCommentCreate {
  CrmTaskId: number;
  Text: string | null;
}

export interface ModelCrmTaskCommentUpdate {
  Id: number;
  Text: string | null;
}

export interface ModelCrmTaskType {
  Id: number;
  IconId: string | null;
  Color: string | null;
  Name: string | null;
}

export interface ModelCrmTask {
  Id: number;
  TaskTypeId: number;
  TaskType: string | null;
  TaskTypeIconId: string | null;
  TaskTypeColor: string | null;
  Description: string | null;
  TimeStart: string;
  TimeDeadline: string;
  IsDeadlineAllDay: boolean;
  Color: string | null;
  IsCompleted: boolean;
  TimeCompleted: string | null;
  Status: CrmTaskStatus;
  StatusName: string | null;
  StatusDescription: string | null;
  Client: ModelClientInfo;
  Owners: ModelUserNames[] | null;
  Comments: ModelCrmTaskComment[] | null;
  UserCreateId: number;
  BranchId?: number | null;
  Branch?: string | null;
}

export interface ModelCrmTaskLite {
  Id: number;
  TaskTypeId: number;
  TaskType: string | null;
  TaskTypeIconId: string | null;
  TaskTypeColor: string | null;
  Description: string | null;
  IsOverdue: boolean;
  TimeDeadline: string;
  IsCompleted: boolean;
  TimeStart: string;
}

export interface ModelCrmTaskCreate {
  TaskTypeId: number;
  Description: string | null;
  TimeStart: string;
  TimeDeadline: string;
  IsDeadlineAllDay: boolean;
  Color: string | null;
  ClientId: number | null;
  OwnerIds: number[] | null;
}

export interface ModelCrmTaskUpdate {
  TaskTypeId: number;
  Description: string | null;
  TimeStart: string;
  TimeDeadline: string;
  IsDeadlineAllDay: boolean;
  Color: string | null;
  ClientId: number | null;
  OwnerIds: number[] | null;
  Id: number;
}

export interface ModelCrmTaskGetForPeriodFilter {
  DateS: string | null;
  DateE: string | null;
  OwnerIds: number[] | null;
}

export interface ModelCrmTaskGetForPeriodResult {
  CrmTasks: ModelCrmTask[] | null;
}

export interface ModelCrmTaskSearchFilter {
  DateS: string | null;
  DateE: string | null;
  OwnerIds: number[] | null;
  PeriodType: CrmTaskPeriodType;
  SearchTerm: string | null;
  CreatorIds: number[] | null;
  ClientIds: number[] | null;
  BranchIds: number[] | null;
  TaskTypeIds: number[] | null;
  Statuses: CrmTaskStatus[] | null;
}

export interface ModelCrmTaskSearchResult {
  Id: number;
  Filter: ModelCrmTaskSearchFilter;
  Rows: ModelCrmTask[] | null;
  Page: number;
  RowsPerPage: number;
  TotalCount: number;
  PageCount: number;
}

export interface ModelBulkCrmTasks {
  CrmTaskIds: number[] | null;
}

export interface ModelBulkCrmTasksSetOwners {
  CrmTaskIds: number[] | null;
  OwnerIds: number[] | null;
}

export interface ModelCrmTaskFilterData {
  Owners: ModelCrmTaskUser[] | null;
  Creators: ModelCrmTaskUser[] | null;
  Clients: ModelCrmTaskClient[] | null;
  Branches: CrmTasksModelItemEx[] | null;
  Statuses: ModelEnum<CrmTaskStatus>[] | null;
  PeriodTypes: ModelEnum<CrmTaskPeriodType>[] | null;
  TaskTypes: ModelCrmTaskType[] | null;
}

export type GetCrmTaskByIdRequest = WithActiveBranchId<{
  id: number;
}>;
export type DeleteCrmTaskByIdRequest = GetCrmTaskByIdRequest;
export type CreateCrmTaskRequest = WithActiveBranchId<{
  data: ModelCrmTaskCreate;
}>;
export type UpdateCrmTaskRequest = WithActiveBranchId<{
  data: ModelCrmTaskUpdate;
}>;
export type CompletedCrmTaskRequest = GetCrmTaskByIdRequest;
export type InProgressCrmTaskRequest = GetCrmTaskByIdRequest;
export type GetCrmTasksForPeriodRequest = WithActiveBranchId<{
  data: ModelCrmTaskGetForPeriodFilter;
}>;
export type SearchCrmTasksRequest = WithActiveBranchId<{
  page: number;
  rowPerPage: number;
  data: ModelCrmTaskSearchFilter;
}>;
export type GetSearchCrmTasksRequest = WithActiveBranchId<{
  searchId: number;
  page: number;
  rowPerPage: number;
}>;
export type SetOwnersForCrmTasksRequest = WithActiveBranchId<{
  data: ModelBulkCrmTasksSetOwners;
}>;
export type SetCompletedForCrmTasksRequest = WithActiveBranchId<{
  data: ModelBulkCrmTasks;
}>;
export type DeleteCrmTasksRequest = WithActiveBranchId<{
  data: ModelBulkCrmTasks;
}>;
export type GetCrmTaskFilterDataRequest = WithActiveBranchId;
export type SearchCrmTaskClientsRequest = WithActiveBranchId<{
  searchTerm: string;
  limit: number;
}>;
export type SearchCrmTaskUsersRequest = SearchCrmTaskClientsRequest;
export type CreateCrmTaskCommentRequest = WithActiveBranchId<{
  data: ModelCrmTaskCommentCreate;
}>;
export type UpdateCrmTaskCommentRequest = WithActiveBranchId<{
  data: ModelCrmTaskCommentUpdate;
}>;
export type GetCrmTaskHistoryByIdRequest = GetCrmTaskByIdRequest;

export type GetCrmTaskByIdResponse = ModelCrmTask;
export type DeleteCrmTaskByIdResponse = void;
export type CreateCrmTaskResponse = ModelCrmTask;
export type UpdateCrmTaskResponse = ModelCrmTask;
export type CompletedCrmTaskResponse = void;
export type InProgressCrmTaskResponse = void;
export type GetCrmTasksForPeriodResponse = ModelCrmTaskGetForPeriodResult;
export type SearchCrmTasksResponse = ModelCrmTaskSearchResult;
export type GetSearchCrmTasksResponse = ModelCrmTaskSearchResult;
export type SetOwnersForCrmTasksResponse = ModelCrmTask[];
export type SetCompletedForCrmTasksResponse = ModelCrmTask[];
export type DeleteCrmTasksResponse = number;
export type GetCrmTaskFilterDataResponse = ModelCrmTaskFilterData;
export type SearchCrmTaskClientsResponse = ModelCrmTaskClient[];
export type SearchCrmTaskUsersResponse = ModelCrmTaskUser[];
export type CreateCrmTaskCommentResponse = ModelCrmTaskComment;
export type UpdateCrmTaskCommentResponse = ModelCrmTaskComment;
export type GetCrmTaskHistoryByIdResponse = ModelActionHistory[];

export type CreateCrmTaskTypeRequest = WithActiveBranchId<{
  data: ModelCrmTaskType;
}>;
export type CreateCrmTaskTypeResponse = ModelCrmTaskType;

export type EditCrmTaskTypeRequest = WithActiveBranchId<{
  data: ModelCrmTaskType;
}>;
export type EditCrmTaskTypeResponse = ModelCrmTaskType;

export type DeleteCrmTaskTypeRequest = WithActiveBranchId<{
  id: number;
}>;
export type DeleteCrmTaskTypeResponse = void;

export type GetCrmTaskTypeByIdRequest = WithActiveBranchId<{
  id: number;
}>;
export type GetCrmTaskTypeByIdResponse = ModelCrmTaskType;

export type GetCrmTaskTypesRequest = WithActiveBranchId<{
  includeId?: number;
}>;
export type GetCrmTaskTypesResponse = ModelCrmTaskType[];
